'use client'

// The one place /alkanes talks to the network.
//
// Every SWR subscription the app has lives here, is created ONCE, and lands in
// the store. Two things follow from that, and both were bugs before it existed:
//
//  * SWR only fires `onSuccess` on the hook that owns a request, so a second
//    `usePools()` anywhere in the tree would silently never stamp `updatedAt`
//    and the staleness chip would lie about how old the prices were.
//  * Everything derived from more than one response — the open asset's row and
//    its market, the USD value of each holding, the resolved swap pair — is
//    computed here and stored as a field. Selectors stay field-reads, which is
//    what keeps a pool refresh from repainting five surfaces.

import React from 'react'
import useSwr, { preload, useSWRConfig } from 'swr'
import { API_URL } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import useAlkanesBalance, {
	fetcher as alkanesBalanceFetcher
} from '@/utils/hooks/useAlkanesBalance'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import {
	BTC_ID,
	CURVE_LP_ID,
	frbtcMidPriceScaled,
	marketForAsset,
	parseAmount,
	tradableTokens,
	type PoolToken
} from './amm'
import useAssets from './useAssets'
import useAssetStats from './useAssetStats'
import usePools from './usePools'
import useActivity, { ACTIVITY_LIMIT } from './useActivity'
import usePoolChart from './usePoolChart'
import useTokenChart from './useTokenChart'
import { useAlkanes, type BalanceRow } from './store'
import { strictFetcher } from './strict-fetcher'

/// Native BTC as a synthetic swap asset: selling it wraps to frBTC, buying it
/// unwraps. It wears the site's own BTC roundel — borrowing the frBTC mark made
/// two different assets look identical in the picker.
const BTC_TOKEN: PoolToken = { id: BTC_ID, name: 'BTC', icon: '/market/btc-collection.svg' }

export default function useAlkanesFeed(watchAddress: string | null): void {
	const { mutate } = useSWRConfig()
	const loggedIn = useLoggedIn()

	// ── pool registry ────────────────────────────────────────────────────────
	const {
		pools,
		sfammPools,
		height: tipHeight,
		routerFeePer1000,
		isLoading: poolsLoading,
		health,
		updatedAt,
		retry: retryPools
	} = usePools()
	const setPools = useAlkanes((s) => s.setPools)
	React.useEffect(() => {
		setPools({
			pools,
			sfammPools,
			tipHeight,
			routerFeePer1000,
			health,
			updatedAt,
			poolsLoading,
			retryPools
		})
	}, [
		setPools,
		pools,
		sfammPools,
		tipHeight,
		routerFeePer1000,
		health,
		updatedAt,
		poolsLoading,
		retryPools
	])

	// ── asset registry ───────────────────────────────────────────────────────
	const { exchangeRate } = useExchangeRate()
	const btcUsd = typeof exchangeRate === 'number' && exchangeRate > 0 ? exchangeRate : null
	const { assets } = useAssets(pools, btcUsd)
	const assetsLoading = poolsLoading && assets.length === 0
	const setAssets = useAlkanes((s) => s.setAssets)
	React.useEffect(() => {
		setAssets({ assets, assetsLoading, btcUsd })
	}, [setAssets, assets, assetsLoading, btcUsd])

	const { data: verificationRegistry } = useSwr<{ verified_ids?: string[] }>(
		`${API_URL}/alkane/verified-assets`,
		strictFetcher,
		{ shouldRetryOnError: false, revalidateOnFocus: false }
	)
	const setVerifiedIds = useAlkanes((s) => s.setVerifiedIds)
	React.useEffect(() => {
		if (Array.isArray(verificationRegistry?.verified_ids)) {
			setVerifiedIds(
				verificationRegistry.verified_ids.filter(
					(id): id is string => typeof id === 'string' && /^\d+:\d+$/.test(id)
				)
			)
		}
	}, [setVerifiedIds, verificationRegistry])

	// ── the open asset ───────────────────────────────────────────────────────
	const assetId = useAlkanes((s) => s.assetId)
	const asset = React.useMemo(
		() => (assetId ? assets.find((a) => a.id === assetId) ?? null : null),
		[assets, assetId]
	)
	// The market behind an asset is DERIVED — its deepest frBTC pool — not
	// chosen. Pools are plumbing, not something a trader should pick between.
	const market = React.useMemo(
		() => (assetId ? marketForAsset(pools, assetId) : null),
		[pools, assetId]
	)
	const setOpenAsset = useAlkanes((s) => s.setOpenAsset)
	React.useEffect(() => {
		setOpenAsset({ asset, market })
	}, [setOpenAsset, asset, market])

	const { stats: assetStats, isLoading: assetStatsLoading } = useAssetStats(assetId)
	const setAssetStats = useAlkanes((s) => s.setAssetStats)
	React.useEffect(() => {
		setAssetStats({ assetStats: assetStats ?? null, assetStatsLoading })
	}, [setAssetStats, assetStats, assetStatsLoading])

	// ── the wallet ───────────────────────────────────────────────────────────
	const address = watchAddress ?? (loggedIn ? Wallet.getTaprootAddress() : undefined)
	const {
		data: balanceItems,
		isLoading: alkanesBalancesLoading,
		error: alkanesBalancesError
	} = useAlkanesBalance(address)

	// BTC lives on the payments address for providers that split wallets;
	// querying taproot there showed 0 while the wallet card showed real sats.
	const btcAddress =
		watchAddress ??
		(loggedIn
			? Wallet.provider?.capabilities?.includes('payments-wallet')
				? Wallet.balanceAddress()
				: Wallet.getTaprootAddress()
			: undefined)
	// Confirmed on-chain BTC (sats). The endpoint's REAL shape is
	// {balance, confirmed_balance, frozen_balance, inscription_balance, …}: this
	// once read `.confirmed` (a field that does not exist), then over-corrected
	// to confirmed − frozen, which zeroed out wallets whose sats ride on
	// inscription-bound UTXOs while the wallet card said otherwise.
	const {
		data: btcBalance,
		isLoading: btcBalanceLoading,
		error: btcBalanceError
	} = useSwr<{ confirmed_balance?: number }>(
		btcAddress ? `${API_URL}/wallet/${btcAddress}/balance` : null,
		strictFetcher
	)
	const balancesLoading = alkanesBalancesLoading || btcBalanceLoading
	const balancesUnavailable = Boolean(
		address && (alkanesBalancesError !== undefined || btcBalanceError !== undefined)
	)

	const balances = React.useMemo(() => {
		const map = new Map<string, bigint>()
		if (Array.isArray(balanceItems)) {
			for (const item of balanceItems) {
				const raw = parseAmount(String(item.overall_balance))
				if (raw !== null && item.rune_id) {
					const id = String(item.rune_id)
					// The frBTCUSD curve LP (4:1778) is 18-decimal by
					// construction — the lone exception to the alkanes-wide
					// 8-dec convention every formatter downstream assumes.
					// Normalize at the door or a held LP renders ×10^10.
					map.set(id, id === CURVE_LP_ID ? raw / 10_000_000_000n : raw)
				}
			}
		}
		// Synthetic BTC balance: confirmed sats (1 sat = 1 raw unit at 8dp).
		const confirmed = btcBalance?.confirmed_balance
		if (typeof confirmed === 'number' && Number.isFinite(confirmed) && confirmed > 0) {
			map.set(BTC_ID, BigInt(Math.floor(confirmed)))
		}
		return map
	}, [balanceItems, btcBalance])

	// USD value of a raw amount via its deepest frBTC route (frBTC ≈ BTC).
	const usdOf = React.useCallback(
		(id: string, raw: bigint): number | null => {
			if (btcUsd === null) {
				return null
			}
			if (id === BTC_ID) {
				return (Number(raw) / 1e8) * btcUsd
			}
			const mid = frbtcMidPriceScaled(pools, id)
			if (mid === null) {
				return null
			}
			// mid is 1e8-scaled frBTC per token; raw is 1e8-scaled tokens.
			return (Number(raw * mid) / 1e24) * btcUsd
		},
		[pools, btcUsd]
	)

	const balancesUsd = React.useMemo(() => {
		const map = new Map<string, number>()
		for (const [id, raw] of balances) {
			const usd = usdOf(id, raw)
			if (usd !== null && usd > 0) {
				map.set(id, usd)
			}
		}
		return map
	}, [balances, usdOf])

	const tokens = React.useMemo(() => [BTC_TOKEN, ...tradableTokens(pools)], [pools])

	const balanceRows = React.useMemo<BalanceRow[]>(() => {
		const rows: BalanceRow[] = []
		// Native BTC leads: it is the biggest thing most of these wallets hold
		// and the unit everything else is measured against.
		const btcRaw = balances.get(BTC_ID)
		if (btcRaw !== undefined && btcRaw > 0n) {
			rows.push({ token: BTC_TOKEN, raw: btcRaw })
		}
		if (Array.isArray(balanceItems)) {
			for (const item of balanceItems) {
				if (!item.rune_id) {
					continue
				}
				const raw = parseAmount(String(item.overall_balance)) ?? 0n
				// A zero-balance row is a ledger artefact, not a holding — and it
				// would also inflate the nav's count badge.
				if (raw <= 0n) {
					continue
				}
				const id = String(item.rune_id)
				const poolToken = tokens.find((t) => t.id === id)
				// An id that IS a pool id is that pool's LP receipt token.
				const lpPool = pools.find((p) => p.pool_id === id)
				rows.push({
					token: {
						id,
						name: item.ticker
							? String(item.ticker)
							: poolToken?.name ?? (lpPool?.name ? String(lpPool.name) : null),
						icon: item.collection?.icon ? String(item.collection.icon) : poolToken?.icon ?? null
					},
					raw
				})
			}
		}
		return rows
	}, [balanceItems, balances, tokens, pools])

	const refreshWallet = React.useCallback(() => {
		if (!address) {
			return
		}
		mutate(['/alkanes-balance', address])
		if (btcAddress) {
			mutate(`${API_URL}/wallet/${btcAddress}/balance`)
		}
		mutate(`${API_URL}/alkane/address/${address}/activity?limit=${ACTIVITY_LIMIT}`)
		const sell = useAlkanes.getState().sell
		if (sell && sell.id !== BTC_ID) {
			mutate(`${API_URL}/wallet/${address}/alkanes-outpoints/${sell.id}`)
		}
	}, [mutate, address, btcAddress])

	const setWallet = useAlkanes((s) => s.setWallet)
	React.useEffect(() => {
		setWallet({
			loggedIn: loggedIn || watchAddress !== null,
			watchAddress,
			address: address ?? null,
			balances,
			balancesUsd,
			balanceRows,
			balancesLoading,
			balancesUnavailable,
			refreshWallet
		})
	}, [
		setWallet,
		loggedIn,
		watchAddress,
		address,
		balances,
		balancesUsd,
		balanceRows,
		balancesLoading,
		balancesUnavailable,
		refreshWallet
	])

	// ── decoded on-chain actions ─────────────────────────────────────────────
	// Fetched once: Activity lists them and Portfolio derives cost basis from
	// the same array, so the two surfaces cannot disagree about one wallet.
	const {
		events: activity,
		isLoading: activityLoading,
		unavailable: activityUnavailable
	} = useActivity(address ?? null)
	const setActivity = useAlkanes((s) => s.setActivity)
	React.useEffect(() => {
		setActivity({ activity, activityLoading, activityUnavailable })
	}, [setActivity, activity, activityLoading, activityUnavailable])

	// ── the swap pair ────────────────────────────────────────────────────────
	const sellPick = useAlkanes((s) => s.sellPick)
	const buyPick = useAlkanes((s) => s.buyPick)
	// Default = buy the open asset with native BTC. The chained wrap-swap made
	// BTC-in a first-class one-click trade, and BTC is what a wallet actually
	// holds; defaulting to frBTC asked people to sell something they do not have.
	const sell = sellPick ?? tokens[0] ?? null
	const buy = buyPick ?? market?.base ?? null
	const setPair = useAlkanes((s) => s.setPair)
	React.useEffect(() => {
		setPair({ tokens, sell, buy })
	}, [setPair, tokens, sell, buy])

	// Warm-on-connect: the moment a wallet connects, prefetch everything the
	// swap panel is about to ask for so the first interaction feels instant.
	const warmedFor = React.useRef<string | null>(null)
	React.useEffect(() => {
		if (!loggedIn || !address || warmedFor.current === address) {
			return
		}
		warmedFor.current = address
		void preload(['/alkanes-balance', address], alkanesBalanceFetcher)
		if (btcAddress) {
			void preload(`${API_URL}/wallet/${btcAddress}/balance`, strictFetcher)
		}
		if (sell && sell.id !== BTC_ID) {
			void preload(`${API_URL}/wallet/${address}/alkanes-outpoints/${sell.id}`, strictFetcher)
		}
	}, [loggedIn, address, btcAddress, sell])

	useChartFeed()
}

/// The chart's two series, fetched here rather than inside the canvas so the
/// chart component owns drawing and nothing else.
///
/// The pool fallback only fetches once the USD feed has answered "no coverage"
/// — not while it is still loading, or every chart would pay for both series on
/// first paint. It also requires the pool's base to BE the open asset: frBTC's
/// fallback market is oriented around its deepest counterparty, and drawing
/// that token's series under an "frBTC price" title would chart the wrong thing.
function useChartFeed(): void {
	const asset = useAlkanes((s) => s.asset)
	const assetId = useAlkanes((s) => s.assetId)
	const market = useAlkanes((s) => s.market)

	const {
		data: tokenChartRaw,
		isLoading: usdLoading,
		error: usdError,
		retry: retryUsd
	} = useTokenChart(assetId, 'all')
	// `keepPreviousData` holds the PREVIOUS asset's series while the next one
	// loads — and forever, if its fetch dies. The response names its own id, so
	// only a series that matches the open asset may reach the chart: drawing
	// DIESEL's candles under ALKAMIST's title is the exact wrong-fact failure
	// the error state below exists to prevent.
	const tokenChart =
		tokenChartRaw !== undefined && tokenChartRaw.id === assetId ? tokenChartRaw : undefined
	const usdCovered = tokenChart?.currency === 'usd' && (tokenChart.candles?.length ?? 0) > 0
	const fallbackFits = !asset || market?.base.id === asset.id
	const wantFallback = tokenChart !== undefined && !usdCovered && fallbackFits
	const poolId = wantFallback ? market?.pool.pool_id ?? null : null
	const {
		data: poolChartRaw,
		isLoading: poolLoading,
		error: poolError,
		retry: retryPool
	} = usePoolChart(poolId, 'all', true)
	const poolChart =
		poolChartRaw !== undefined && poolChartRaw.pool_id === poolId ? poolChartRaw : undefined
	const chartLoading = usdLoading || (wantFallback && poolLoading)
	// An error only counts while it leaves the chart with nothing CURRENT to
	// draw — a failed 60s refresh on top of this asset's own cached candles
	// keeps drawing them. A dead USD fetch means the fallback never got to
	// answer, so either failure forbids the "no history" claim.
	const chartError =
		!chartLoading &&
		((tokenChart === undefined && usdError !== undefined) ||
			(wantFallback && poolChart === undefined && poolError !== undefined))

	const setChart = useAlkanes((s) => s.setChart)
	React.useEffect(() => {
		setChart({
			tokenChart: tokenChart ?? null,
			poolChart: poolChart ?? null,
			usdCovered,
			chartLoading,
			chartError,
			retryChart: () => {
				retryUsd()
				retryPool()
			}
		})
		// retryUsd/retryPool are stable closures over SWR's bound mutate; the
		// data deps below are what actually change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setChart, tokenChart, poolChart, usdCovered, chartLoading, chartError])
}
